import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AUTH_VISUAL = "https://static.prod-images.emergentagent.com/jobs/5c54ef11-29b1-4ebe-ae66-9bbd6fa3e33c/images/48f0e20ec19d1ca96ff7da02adf5395c52c2dc9d699fc4d20e6b90ff06324d97.png";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            nav("/app/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = () => {
        const redirect = `${window.location.origin}/auth/callback`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-white">
            {/* Left: form */}
            <div className="flex flex-col p-8 sm:p-12 lg:p-16">
                <Link to="/" data-testid="login-logo" className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-display font-black text-sm">R</div>
                    <span className="font-display font-bold tracking-tight">RepurposeAI</span>
                </Link>

                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto lg:mx-0">
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">// SIGN IN</div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Welcome back.</h1>
                    <p className="mt-3 text-zinc-400">Repurpose your next idea in 30 seconds.</p>

                    <Button
                        data-testid="login-google-btn"
                        type="button"
                        onClick={googleLogin}
                        variant="outline"
                        className="mt-8 h-11 w-full bg-transparent border-white/15 hover:bg-white/5 text-white"
                    >
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1H12v3.2h5.35c-.2 1.4-1.6 4.1-5.35 4.1-3.2 0-5.85-2.65-5.85-5.9s2.65-5.9 5.85-5.9c1.85 0 3.05.8 3.75 1.45l2.55-2.45C16.6 4.05 14.55 3 12 3 6.95 3 3 6.95 3 12s3.95 9 9 9c5.2 0 8.65-3.65 8.65-8.8 0-.6-.05-1.05-.15-1.1z"/></svg>
                        Continue with Google
                    </Button>

                    <div className="my-6 flex items-center gap-3">
                        <div className="flex-1 h-px bg-white/10" />
                        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-600">OR</div>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">Email</Label>
                            <Input
                                data-testid="login-email-input"
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-2 h-11 bg-transparent border-white/15 focus:border-white text-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">Password</Label>
                            <Input
                                data-testid="login-password-input"
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="mt-2 h-11 bg-transparent border-white/15 focus:border-white text-white"
                            />
                        </div>
                        <Button
                            data-testid="login-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-semibold"
                        >
                            {loading ? "Signing in…" : "Sign in →"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-zinc-500">
                        <Link to="/forgot-password" data-testid="login-forgot-link" className="text-white underline-offset-4 hover:underline">Forgot password?</Link>
                    </p>
                    <p className="mt-2 text-sm text-zinc-500">
                        No account?{" "}
                        <Link to="/register" data-testid="login-to-register" className="text-white underline-offset-4 hover:underline">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right: visual */}
            <div className="hidden lg:block relative">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${AUTH_VISUAL})` }} />
                <div className="absolute inset-0 bg-gradient-to-l from-black/0 via-black/30 to-black/60" />
                <div className="relative h-full flex flex-col justify-end p-12">
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-300">// CONTENT, EVERYWHERE</div>
                    <p className="mt-4 font-display font-bold text-3xl tracking-tight text-white max-w-md">
                        "I turn a single YouTube video into a full week of posts."
                    </p>
                    <p className="mt-3 text-zinc-400">— Lena Park, indie creator</p>
                </div>
            </div>
        </div>
    );
}
